import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mindBodyAuth } from 'services/auth/mindbody-auth';
import { useAuthStore } from 'stores/auth-store';
import { useUIStore } from 'stores/ui-store';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, siteId: number) => Promise<void>;
  logout: () => void;
  validateSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const { user, setUser, clearUser, setLoading: setAuthLoading } = useAuthStore();
  const { addNotification } = useUIStore();
  const [isInitializing, setIsInitializing] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      setIsInitializing(true);
      
      try {
        // Check if we have a valid session
        if (mindBodyAuth.isAuthenticated()) {
          const isValid = await mindBodyAuth.validateSession();
          
          if (isValid) {
            const currentUser = mindBodyAuth.getCurrentUser();
            if (currentUser) {
              setUser({
                id: currentUser.id,
                email: currentUser.email,
                name: `${currentUser.firstName} ${currentUser.lastName}`,
                role: currentUser.role as any,
                studioIds: currentUser.studioIds,
              });
            }
          } else {
            // Session is invalid, clear auth state
            mindBodyAuth.logout();
            clearUser();
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        clearUser();
      } finally {
        setIsInitializing(false);
      }
    };

    checkAuth();
  }, [setUser, clearUser]);

  const login = async (username: string, password: string, siteId: number) => {
    setAuthLoading(true);
    
    try {
      const response = await mindBodyAuth.login({
        username,
        password,
        siteId,
      });

      // Update auth store
      setUser({
        id: response.user.id,
        email: response.user.email,
        name: `${response.user.firstName} ${response.user.lastName}`,
        role: response.user.role as any,
        studioIds: response.user.studioIds,
      });

      addNotification({
        type: 'success',
        title: 'Login Successful',
        message: `Welcome back, ${response.user.firstName}!`,
      });

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Login Failed',
        message: error.message || 'Invalid credentials',
      });
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    mindBodyAuth.logout();
    clearUser();
    
    addNotification({
      type: 'info',
      title: 'Logged Out',
      message: 'You have been logged out successfully',
    });
    
    navigate('/login');
  };

  const validateSession = async (): Promise<boolean> => {
    try {
      const isValid = await mindBodyAuth.validateSession();
      if (!isValid) {
        clearUser();
      }
      return isValid;
    } catch {
      clearUser();
      return false;
    }
  };

  const value: AuthContextType = {
    isAuthenticated: !!user,
    isLoading: isInitializing,
    login,
    logout,
    validateSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}